import React, { useState, useEffect } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box, Button, Avatar, Typography } from '@mui/material';
import { Session } from './sessionTypes';
import {
  formatSessionActualStartLabel,
  formatSessionEndLabel,
  getSessionAttendancePercent,
  getSessionDeliveryTypeLabel,
  sessionNameSubtitle,
  sessionTablePaperSx,
  sessionTableContainerSx,
} from './sessionUtils';
import { SessionIdBadge } from './SessionIdBadge';

interface CompletedSessionsProps {
  sessions: Session[];
  batchLabel?: string;
  onShowReport: (session: Session) => void;
  onSessionsChange?: (sessions: Session[]) => void;
  pagination: { pageIndex: number; pageSize: number };
  rowCount: number;
  onPaginationChange: (pagination: { pageIndex: number; pageSize: number }) => void;
}

const CompletedSessions: React.FC<CompletedSessionsProps> = ({
  sessions,
  batchLabel = '',
  onShowReport,
  onSessionsChange,
  pagination,
  rowCount,
  onPaginationChange,
}) => {
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!onSessionsChange) return;
    const selectedSessions = sessions.filter((s) => rowSelection[s.id.toString()]);
    onSessionsChange(selectedSessions);
  }, [rowSelection, sessions, onSessionsChange]);

  const columns = [
    {
      accessorKey: 'Session_Topic',
      header: 'SESSION NAME',
      Cell: ({ row }: { row: { original: Session } }) => {
        const session = row.original;
        const subtitle = sessionNameSubtitle(session, batchLabel);
        return (
          <Box>
            <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: 14, lineHeight: 1.3 }}>
              {session.Session_Topic}
            </Typography>
            <SessionIdBadge sessionId={session.id} />
            {subtitle ? (
              <Typography sx={{ color: '#64748b', fontSize: 12, mt: 0.25 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        );
      },
    },
    {
      accessorKey: 'conductedby',
      header: 'TRAINER',
      Cell: ({ row }: { row: { original: Session } }) => {
        const name = row.original.conductedby || '—';
        const initial = name.trim().charAt(0).toUpperCase() || '?';
        const substitute = (row.original.co_trainers || [])[0];
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: '#e2e8f0', color: '#334155' }}>
              {initial}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: 14, color: '#0f172a' }}>{name}</Typography>
              {substitute ? (
                <Typography sx={{ fontSize: 12, color: '#2563eb' }}>Sub: {substitute}</Typography>
              ) : null}
            </Box>
          </Box>
        );
      },
    },
    {
      id: 'type',
      header: 'TYPE',
      Cell: ({ row }: { row: { original: Session } }) => (
        <Typography sx={{ fontSize: 14, color: '#334155' }}>
          {getSessionDeliveryTypeLabel(row.original)}
        </Typography>
      ),
    },
    {
      id: 'start',
      header: 'START',
      Cell: ({ row }: { row: { original: Session } }) => (
        <Typography sx={{ fontSize: 14, color: '#334155', whiteSpace: 'nowrap' }}>
          {formatSessionActualStartLabel(row.original)}
        </Typography>
      ),
    },
    {
      id: 'end',
      header: 'END',
      Cell: ({ row }: { row: { original: Session } }) => (
        <Typography sx={{ fontSize: 14, color: '#334155', whiteSpace: 'nowrap' }}>
          {formatSessionEndLabel(row.original)}
        </Typography>
      ),
    },
    {
      id: 'duration',
      header: 'DURATION',
      Cell: ({ row }: { row: { original: Session } }) => {
        const mins = Math.round(Number(row.original.session_duration));
        return (
          <Typography sx={{ fontSize: 14, color: '#334155' }}>
            {Number.isFinite(mins) && mins > 0 ? `${mins} min` : '—'}
          </Typography>
        );
      },
    },
    {
      id: 'attendance',
      header: 'ATTENDANCE',
      Cell: ({ row }: { row: { original: Session } }) => (
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#16a34a' }}>
          {getSessionAttendancePercent(row.original)}
        </Typography>
      ),
    },
    {
      id: 'actions',
      header: 'ACTIONS',
      Cell: ({ row }: { row: { original: Session } }) => (
        <Button
          size="small"
          onClick={() => onShowReport(row.original)}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            color: '#2563eb',
            borderRadius: '8px',
            px: 1.25,
            '&:hover': { backgroundColor: '#eff6ff' },
          }}
        >
          Report
        </Button>
      ),
    },
  ];

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <MaterialReactTable
        columns={columns}
        data={sessions}
        enableColumnFilters={false}
        enableRowSelection={false}
        state={{ pagination }}
        manualPagination
        rowCount={rowCount}
        onPaginationChange={(updater: any) => {
          const next = typeof updater === 'function' ? updater(pagination) : updater;
          onPaginationChange(next);
        }}
        getRowId={(row) => row.id.toString()}
        muiTablePaperProps={{ sx: { ...sessionTablePaperSx } }}
        enableTopToolbar={false}
        muiTableHeadCellProps={{
          sx: {
            color: '#94a3b8',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.04em',
            backgroundColor: '#fff',
            borderBottom: '1px solid #e5e7eb',
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            borderBottom: '1px solid #f1f5f9',
            py: 1.5,
          },
        }}
        muiTableContainerProps={{ sx: { ...sessionTableContainerSx } }}
        initialState={{ density: 'comfortable' }}
      />
    </Box>
  );
};

export default CompletedSessions;
